import * as FullStory from "@fullstory/browser";
import { AnalyticsEvent, LoanOfficerDetail, StepResponse } from "pages/application/applicationService";
import { RegistrationRoleType } from "pages/registration/registrationService";
import {
  GUIDED_EXP_TASK_COMPLETE_SEEN_STATE,
  GUIDED_EXP_TASK_COMPLETE_TRACKED,
} from "utilities/Constant";

let isFullStoryShutdown = false;
let leadContextTagged = false;
let cachedLeadProvider: string | undefined;

const isFullStoryEnabled = () => globalThis.env.ENABLE_FULL_STORY && !isFullStoryShutdown;

/** Module-level set — shared across all E-Sign tracking code for the session lifetime.
 *  Prevents double-firing when multiple components/hooks attempt to track the same event.
 *  Survives component remounts and ensures the event fires exactly once per package per session. */
export const sessionESignEventKeys = new Set<string>();

export const FLOW_CONTEXT = {
  login: "login",
  registration: "registration",
  self_registration: "self_registration",
  co_borrower_registration: "co_borrower_registration",
  user_gateway: "user_gateway",
  password_reset: "password_reset",
  forced_password_change: "forced_password_change",
  recover: "recover",
} as const;

export type FlowContext = (typeof FLOW_CONTEXT)[keyof typeof FLOW_CONTEXT];

export const SESSION_TYPE = {
  borrower: "borrower",
  impersonation: "impersonation",
} as const;

export type SessionType = (typeof SESSION_TYPE)[keyof typeof SESSION_TYPE];

export const AUTH_OUTCOME = {
  success: "success",
  failure: "failure",
  otp_required: "otp_required",
  completed: "completed",
  attempted: "attempted",
  redirected: "redirected",
} as const;

export type AuthOutcome = (typeof AUTH_OUTCOME)[keyof typeof AUTH_OUTCOME];

export type LeadBorrowerType = "PB" | "SCB" | "NSCB";

export const REGISTRATION_RAIL = {
  invite: "invite",
  co_borrower: "co_borrower",
  self_registration: "self_registration",
  user_gateway: "user_gateway",
} as const;

export type RegistrationRail = (typeof REGISTRATION_RAIL)[keyof typeof REGISTRATION_RAIL];

export const REGISTRATION_EVENT = {
  visited: "Visited Registration Page",
  visitedSelf: "Visited Self Registration Page",
  submitted: "Registration Submitted",
  completed: "Registration Completed",
  failed: "Registration Failed",
} as const;

export interface RegistrationFunnelBaseProps {
  flow_context: FlowContext;
  registrationRail: RegistrationRail;
  leadProvider?: string | null;
  loanType?: string | null;
  borrowerType?: string | null;
  /** Raw registration role from decrypt/API (e.g. Borrower, SpousalCoBorrower) for event labeling. */
  borrowerRole?: string | null;
}

/** Legacy visit payload from pre–522835 `Registration Page Visited` (kept for dashboard continuity). */
export interface VisitedRegistrationPageProps extends RegistrationFunnelBaseProps {
  referringUrl?: string;
  salesChannel?: string;
  loFullName?: string | null;
  loNmls?: string | null;
  state?: string;
  loCardVisible?: boolean;
}

const AUTH_EVENT_FORBIDDEN_KEYS = new Set([
  "email",
  "Email",
  "lastName",
  "LastName",
  "password",
  "otp",
  "token",
  "accessToken",
  "refreshToken",
  "responseBody",
  "body",
  "loEmail",
  "LOEmail",
  "loPhone",
  "LOPhone",
  "photo",
  "photoImageUrl",
  "headshotUrl",
]);

export interface AuthEventProperties {
  flow_context: FlowContext;
  Outcome: AuthOutcome;
  FailureCode?: string | number;
}

export interface LeadContextUserVars {
  lead_provider?: string;
  borrower_type?: LeadBorrowerType;
  nh_deal_application_id?: string;
}

export interface RegistrationSubmitLoProperties {
  LoanOfficerName?: string;
  LoanOfficerNMLS?: string;
}

const REDUX_PERSIST_ROOT_KEY = "persist:root";

export function isPersistedImpersonationSession(): boolean {
  try {
    const raw = sessionStorage.getItem(REDUX_PERSIST_ROOT_KEY);
    if (!raw) return false;
    const root = JSON.parse(raw) as { auth?: string };
    if (typeof root.auth !== "string") return false;
    const auth = JSON.parse(root.auth) as { isImpersonating?: boolean };
    return auth.isImpersonating === true;
  } catch {
    return false;
  }
}

export function isUnknownRegistrationRole(role?: string | null): boolean {
  if (!role?.trim()) return true;
  return role.trim().toLowerCase() === RegistrationRoleType.Unknown.toLowerCase();
}

export function mapLeadBorrowerType(role?: string | null): LeadBorrowerType | undefined {
  if (isUnknownRegistrationRole(role)) return undefined;
  const normalized = role?.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === RegistrationRoleType.Borrower.toLowerCase() || normalized === "pb" || normalized === "primaryborrower") {
    return "PB";
  }
  if (normalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase() || normalized === "scb") {
    return "SCB";
  }
  if (
    normalized === RegistrationRoleType.NonSpousalCoBorrower.toLowerCase() ||
    normalized === RegistrationRoleType.CoBorrower.toLowerCase() ||
    normalized === "nscb"
  ) {
    return "NSCB";
  }
  return undefined;
}

/** Registration funnel events use Primary/Spouse/NSCB to align with Application Started. */
export function formatRegistrationEventBorrowerType(
  borrowerTypeCode?: LeadBorrowerType | string | null,
  rawRole?: string | null
): string {
  const roleForFormat = isUnknownRegistrationRole(rawRole) ? undefined : rawRole;
  const typeCodeForFormat =
    typeof borrowerTypeCode === "string" && isUnknownRegistrationRole(borrowerTypeCode)
      ? undefined
      : borrowerTypeCode;
  const mappedFromCode =
    typeCodeForFormat === "PB" || typeCodeForFormat === "SCB" || typeCodeForFormat === "NSCB"
      ? typeCodeForFormat
      : mapLeadBorrowerType(typeCodeForFormat);
  const mapped = mappedFromCode ?? mapLeadBorrowerType(roleForFormat);

  if (mapped === "PB") return "Primary";
  if (mapped === "SCB") return "Spouse";
  if (mapped === "NSCB") return "NSCB";

  const normalized = (roleForFormat ?? typeCodeForFormat ?? "").toString().trim().toLowerCase();
  if (!normalized) return "Unknown";
  if (normalized === "spousalcoborrower" || normalized === "spouse") return "Spouse";
  if (
    normalized === "nonspousalcoborrower" ||
    normalized === "coborrower" ||
    normalized === "2" ||
    normalized === "nscb"
  ) {
    return "NSCB";
  }
  if (
    normalized === "borrower" ||
    normalized === "pb" ||
    normalized === "primaryborrower" ||
    normalized === "primary"
  ) {
    return "Primary";
  }
  return "Unknown";
}

export function resolveRegistrationRail(input: {
  route: "registration" | "registrationCoBorrower" | "external-registration";
  hasInviteToken?: boolean;
  fromUserGateway?: boolean;
  hasEmailFromStore?: boolean;
}): RegistrationRail {
  if (input.route === "registrationCoBorrower") return REGISTRATION_RAIL.co_borrower;
  if (input.route === "external-registration") return REGISTRATION_RAIL.self_registration;
  if (input.fromUserGateway || (!input.hasInviteToken && input.hasEmailFromStore)) {
    return REGISTRATION_RAIL.user_gateway;
  }
  return REGISTRATION_RAIL.invite;
}

export function flowContextForRegistrationRail(rail: RegistrationRail): FlowContext {
  if (rail === REGISTRATION_RAIL.co_borrower) return FLOW_CONTEXT.co_borrower_registration;
  if (rail === REGISTRATION_RAIL.self_registration) return FLOW_CONTEXT.self_registration;
  if (rail === REGISTRATION_RAIL.user_gateway) return FLOW_CONTEXT.user_gateway;
  return FLOW_CONTEXT.registration;
}

export function mapRegistrationOutcome(
  status?: string | null,
  isMfaSkipEnabled?: boolean
): Extract<AuthOutcome, "otp_required" | "completed"> | undefined {
  if (isMfaSkipEnabled) return AUTH_OUTCOME.completed;
  const normalized = status?.trim().toLowerCase();
  if (normalized === "completed") return AUTH_OUTCOME.completed;
  if (normalized === "otp_required" || normalized === "device_selection_required") return AUTH_OUTCOME.otp_required;
  return undefined;
}

export function toAuthFailureCode(error: unknown): string | number {
  if (typeof error === "number" && Number.isFinite(error)) return error;
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (trimmed.length > 0 && trimmed.length <= 64 && !trimmed.includes(" ")) return trimmed;
    return "unknown";
  }
  if (error && typeof error === "object") {
    const status = (error as { status?: unknown }).status;
    if (typeof status === "number" && Number.isFinite(status)) return status;
    const code = (error as { data?: { code?: unknown } }).data?.code;
    if (typeof code === "string" && code.trim().length > 0 && !code.includes(" ")) return code.trim();
  }
  return "unknown";
}

export function setFSLeadContextUserVars(vars: LeadContextUserVars) {
  if (!isFullStoryEnabled() || leadContextTagged) return;

  const payload: Record<string, string> = {};
  if (vars.lead_provider) {
    payload.lead_provider = vars.lead_provider;
    cachedLeadProvider = vars.lead_provider;
  }
  if (vars.borrower_type) payload.borrower_type = vars.borrower_type;
  if (vars.nh_deal_application_id) payload.nh_deal_application_id = vars.nh_deal_application_id;
  if (Object.keys(payload).length === 0) return;

  leadContextTagged = true;
  FullStory.setUserVars(payload);
}

export function tagFSLeadContextFromDecrypt(user: {
  lpn?: string | null;
  rt?: string | null;
  nhDealAppId?: string | null;
}) {
  setFSLeadContextUserVars({
    lead_provider: user.lpn ?? undefined,
    borrower_type: mapLeadBorrowerType(user.rt),
    nh_deal_application_id: user.nhDealAppId ?? undefined,
  });
}

export const LEAD_FUNNEL_EVENT = {
  linkOpened: "Lead Link Opened",
  decryptCompleted: "Lead Decrypt Completed",
  routed: "Lead Routed",
} as const;

export type LeadEntryRoute =
  | "/"
  | "/registration"
  | "/registrationCoBorrower"
  | "/external-registration"
  | "/start";

export const LEAD_DESTINATION = {
  login: "login",
  registration: "registration",
  registration_co_borrower: "registration_co_borrower",
  external_registration: "external_registration",
  error: "error",
} as const;

export type LeadDestination = (typeof LEAD_DESTINATION)[keyof typeof LEAD_DESTINATION];

const ANALYTICS_LEAD_PROVIDERS = new Set(["NAFLink", "Microsite", "Bonzo", "Digital"]);

export function normalizeLeadProviderForEvent(raw?: string | null): string {
  if (!raw?.trim()) return "Unknown";
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  if (lower === "naflink" || lower === "naf link") return "NAFLink";
  if (lower === "microsite") return "Microsite";
  if (lower === "bonzo") return "Bonzo";
  if (lower === "digital") return "Digital";
  if (ANALYTICS_LEAD_PROVIDERS.has(trimmed)) return trimmed;
  return "Unknown";
}

export function resolvePreAuthFlowContext(entryRoute: LeadEntryRoute): FlowContext {
  switch (entryRoute) {
    case "/":
      return FLOW_CONTEXT.login;
    case "/registration":
      return FLOW_CONTEXT.registration;
    case "/registrationCoBorrower":
    case "/external-registration":
      return FLOW_CONTEXT.co_borrower_registration;
    case "/start":
      return FLOW_CONTEXT.user_gateway;
  }
}

export function resolveLeadStayDestination(entryRoute: LeadEntryRoute): LeadDestination {
  switch (entryRoute) {
    case "/registration":
      return LEAD_DESTINATION.registration;
    case "/registrationCoBorrower":
      return LEAD_DESTINATION.registration_co_borrower;
    case "/external-registration":
      return LEAD_DESTINATION.external_registration;
    default:
      return LEAD_DESTINATION.registration;
  }
}

export interface LeadDecryptPayload {
  lpn?: string | null;
  lp?: string | null;
  rt?: string | null;
  nhDealAppId?: string | null;
  ex?: boolean;
}

export function buildRegistrationFunnelLeadFields(
  data?: LeadDecryptPayload | null,
  overrides?: {
    leadProvider?: string | null;
    loanType?: string | null;
    borrowerType?: LeadBorrowerType | string | null;
    borrowerRole?: string | null;
  }
): Pick<RegistrationFunnelBaseProps, "leadProvider" | "loanType" | "borrowerType" | "borrowerRole"> {
  const borrowerRole = !isUnknownRegistrationRole(data?.rt)
    ? data?.rt ?? undefined
    : overrides?.borrowerRole ?? undefined;
  const borrowerFromRole = mapLeadBorrowerType(borrowerRole);
  const borrowerOverride =
    typeof overrides?.borrowerType === "string" &&
    overrides.borrowerType.length > 0 &&
    !isUnknownRegistrationRole(overrides.borrowerType)
      ? overrides.borrowerType
      : undefined;

  return {
    leadProvider: data?.lpn ?? overrides?.leadProvider ?? undefined,
    loanType: data?.lp ?? overrides?.loanType ?? undefined,
    borrowerType: borrowerOverride ?? borrowerFromRole ?? undefined,
    borrowerRole,
  };
}

export interface LeadLinkOpenedProperties {
  entryRoute: LeadEntryRoute;
  leadProvider?: string | null;
  hasEncryptedToken: boolean;
}

export function trackFSLeadLinkOpened({ entryRoute, leadProvider, hasEncryptedToken }: LeadLinkOpenedProperties) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(LEAD_FUNNEL_EVENT.linkOpened, {
    flow_context: resolvePreAuthFlowContext(entryRoute),
    LeadProvider: normalizeLeadProviderForEvent(leadProvider),
    HasEncryptedToken: hasEncryptedToken,
    EntryRoute: entryRoute,
  });
}

export interface LeadDecryptCompletedProperties {
  entryRoute: LeadEntryRoute;
  outcome: typeof AUTH_OUTCOME.success | typeof AUTH_OUTCOME.failure;
  isExpired: boolean;
  leadProvider?: string | null;
  leadCampaign?: string | null;
  loanType?: string | null;
  borrowerType?: LeadBorrowerType;
  failureCode?: string | number;
}

export function trackFSLeadDecryptCompleted(props: LeadDecryptCompletedProperties) {
  if (!isFullStoryEnabled()) return;

  const flow_context = resolvePreAuthFlowContext(props.entryRoute);
  FullStory.event(LEAD_FUNNEL_EVENT.decryptCompleted, {
    flow_context,
    Outcome: props.outcome,
    IsExpired: props.isExpired,
    LeadProvider: normalizeLeadProviderForEvent(props.leadProvider),
    ...(props.leadCampaign ? { LeadCampaign: props.leadCampaign } : {}),
    ...(props.loanType ? { LoanType: props.loanType } : {}),
    ...(props.borrowerType ? { BorrowerType: props.borrowerType } : {}),
    ...(props.failureCode !== undefined ? { FailureCode: props.failureCode } : {}),
  });
}

export interface LeadRoutedProperties {
  entryRoute: LeadEntryRoute;
  destination: LeadDestination;
  leadProvider?: string | null;
}

export function trackFSLeadRouted({ entryRoute, destination, leadProvider }: LeadRoutedProperties) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(LEAD_FUNNEL_EVENT.routed, {
    flow_context: resolvePreAuthFlowContext(entryRoute),
    Destination: destination,
    LeadProvider: normalizeLeadProviderForEvent(leadProvider),
  });
}

export function applyLeadUserVarsFromDecrypt(payload: LeadDecryptPayload, leadProviderFromUrl?: string | null) {
  if (payload.lpn != null || payload.rt != null) {
    tagFSLeadContextFromDecrypt({
      lpn: payload.lpn ?? leadProviderFromUrl,
      rt: payload.rt,
      nhDealAppId: payload.nhDealAppId,
    });
    return;
  }
  if (payload.nhDealAppId) {
    setFSLeadContextUserVars({ nh_deal_application_id: payload.nhDealAppId });
  }
}

export function setFSSessionType(sessionType: SessionType) {
  if (!isFullStoryEnabled()) return;
  FullStory.setUserVars({ session_type: sessionType });
}

export function buildRegistrationSubmitLoProperties(lo?: {
  fullName?: string | null;
  nmlsLicenseNumber?: string | null;
}): RegistrationSubmitLoProperties {
  return {
    ...(lo?.fullName ? { LoanOfficerName: lo.fullName } : {}),
    ...(lo?.nmlsLicenseNumber ? { LoanOfficerNMLS: lo.nmlsLicenseNumber } : {}),
  };
}

function omitForbiddenAuthEventKeys(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !AUTH_EVENT_FORBIDDEN_KEYS.has(key))
  );
}

export function trackFSAuthEvent(eventName: string, properties: AuthEventProperties & Record<string, unknown>) {
  if (!isFullStoryEnabled()) return;

  const { flow_context, Outcome, FailureCode, ...rest } = properties;
  FullStory.event(eventName, {
    flow_context,
    Outcome,
    ...(FailureCode !== undefined ? { FailureCode } : {}),
    ...omitForbiddenAuthEventKeys(rest),
  });
}

export const AUTH_STEP_EVENT = "Auth Step";
export const AUTH_ERROR_EVENT = "Auth Error";

export function trackFSAuthStep(flowContext: FlowContext, outcome: Exclude<AuthOutcome, "failure">) {
  trackFSAuthEvent(AUTH_STEP_EVENT, {
    flow_context: flowContext,
    Outcome: outcome,
  });
}

export function trackFSAuthError(flowContext: FlowContext, error: unknown) {
  trackFSAuthEvent(AUTH_ERROR_EVENT, {
    flow_context: flowContext,
    Outcome: AUTH_OUTCOME.failure,
    FailureCode: toAuthFailureCode(error),
  });
}

export const LOGIN_EVENT = {
  attempted: "Login Attempted",
  completed: "Login Completed",
  failed: "Login Failed",
  redirected: "Login Redirected",
} as const;

export const LOGIN_REDIRECT_REASON = {
  mfa: "mfa",
  otp_required: "otp_required",
  must_change_password: "must_change_password",
} as const;

export type LoginRedirectReason = (typeof LOGIN_REDIRECT_REASON)[keyof typeof LOGIN_REDIRECT_REASON];

export function trackFSLoginAttempted() {
  trackFSAuthEvent(LOGIN_EVENT.attempted, {
    flow_context: FLOW_CONTEXT.login,
    Outcome: AUTH_OUTCOME.attempted,
    ...(cachedLeadProvider ? { LeadProvider: cachedLeadProvider } : {}),
  });
}

export function trackFSLoginFailed(error: unknown) {
  trackFSAuthEvent(LOGIN_EVENT.failed, {
    flow_context: FLOW_CONTEXT.login,
    Outcome: AUTH_OUTCOME.failure,
    FailureCode: toAuthFailureCode(error),
  });
}

export function trackFSLoginCompleted() {
  trackFSAuthEvent(LOGIN_EVENT.completed, {
    flow_context: FLOW_CONTEXT.login,
    Outcome: AUTH_OUTCOME.completed,
    MfaRequired: false,
  });
}

export function trackFSLoginRedirected(reason: LoginRedirectReason) {
  trackFSAuthEvent(LOGIN_EVENT.redirected, {
    flow_context: FLOW_CONTEXT.login,
    Outcome: AUTH_OUTCOME.redirected,
    Reason: reason,
  });
}

/**
 * Immediately disables Full Story data capture
 */
export const shutdownFullStory = () => {
  isFullStoryShutdown = true;
  FullStory.shutdown();
};

const initFullStory = () => {
  isFullStoryShutdown = false;
  leadContextTagged = false;
  cachedLeadProvider = undefined;
  if (!globalThis.env.ENABLE_FULL_STORY) return;

  if (isPersistedImpersonationSession()) {
    shutdownFullStory();
    return;
  }

  FullStory.init({ orgId: globalThis.env.FULLSTORY_ORG_ID });
  FullStory.setUserVars({ session_type: SESSION_TYPE.borrower });
};

/**
 * @param stepCode internal code for the step
 * @param stepLabel String name for StepCode
 * @param skipped true if user skip
 */
export function trackFSStepCompleted(stepCode: string, stepLabel: string, skipped: boolean) {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Step completed", {
    StepCode: stepCode,
    StepName: stepLabel,
    Skipped: skipped ? "Yes" : "No",
  });
}

export function trackFSApplicationStarted(props: {
  leadProvider?: string;
  loanType?: string;
  pathSelected?: string;
  leadCampaign?: string;
  applicationStartedDate: string;
  loanOfficerAssigned?: string;
  borrowerType?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, loanType, pathSelected, leadCampaign, loanOfficerAssigned, applicationStartedDate, borrowerType } = props;

  FullStory.event("Application Started", {
    StartedDate: applicationStartedDate,
    LeadProvider: leadProvider ?? "Unknown",
    LeadCampaign: leadCampaign ?? "Unknown",
    LoanType: loanType ?? "Unknown",
    PathSelected: pathSelected ?? "Unknown",
    LoanOfficerAssigned: loanOfficerAssigned ?? "Not Assigned",
    Environment: globalThis.env.ENVIRONMENT ?? "Unknown",
    borrowerType: borrowerType,
  });
}

const trackFSSummaryEvent = (eventName: string, eventPayload: Record<string, unknown>) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, { ...eventPayload });
};

const trackFSSummaryEditEvent = (stepCode: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Summary Page Edited", {
    SummaryPage: stepCode,
  });
};

export const trackFSInitAppSubmittedEvent = () => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Initial App Submitted", {
    DateSubmitted: new Date().toISOString(),
  });
};

const trackFSOptedVoaEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted Voa", { VoaOrderDate: orderDate });
};

const trackFSOptedVoiEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted VOI", { VoiOrderDate: orderDate });
};

const trackFSLOAssignedEvent = (loanOfficerDetails: LoanOfficerDetail, stepData: StepResponse) => {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Loan Officer Assigned", {
    LoanOfficerName: loanOfficerDetails.fullName,
    LoanOfficerNMLS: loanOfficerDetails.nmlsLicenseNumber,
    StepCode: stepData.stepCodeText,
    Section: stepData.section,
  });
};

function buildRegistrationFunnelBasePayload(props: RegistrationFunnelBaseProps) {
  return omitForbiddenAuthEventKeys({
    flow_context: props.flow_context,
    RegistrationRail: props.registrationRail,
    LeadProvider: props.leadProvider || "Unknown",
    BorrowerType: formatRegistrationEventBorrowerType(props.borrowerType, props.borrowerRole),
  });
}

export function trackFSVisitedRegistrationPage(props: VisitedRegistrationPageProps) {
  if (!isFullStoryEnabled()) return;

  const loanPurpose = props.loanType || "Unknown";

  FullStory.event(REGISTRATION_EVENT.visited, {
    ...buildRegistrationFunnelBasePayload(props),
    LoanType: loanPurpose,
    LoanPurpose: loanPurpose,
    ReferringUrl: props.referringUrl ?? "Unknown",
    SalesChannel: props.salesChannel ?? "Unknown",
    ...buildRegistrationSubmitLoProperties({ fullName: props.loFullName, nmlsLicenseNumber: props.loNmls }),
    State: props.state ?? "Unknown",
    LOCardVisible: props.loCardVisible ?? false,
  });
}

export function trackFSVisitedSelfRegistrationPage(props: Pick<RegistrationFunnelBaseProps, "flow_context" | "leadProvider" | "loanType">) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(REGISTRATION_EVENT.visitedSelf, omitForbiddenAuthEventKeys({
    flow_context: props.flow_context,
    LeadProvider: props.leadProvider || "Unknown",
    LoanType: props.loanType || "Unknown",
  }));
}

export function trackFSRegistrationPageRendered(props: VisitedRegistrationPageProps) {
  trackFSVisitedRegistrationPage(props);
  if (props.registrationRail === REGISTRATION_RAIL.self_registration) {
    trackFSVisitedSelfRegistrationPage(props);
  }
}

export function trackFSRegistrationSubmitted(
  props: RegistrationFunnelBaseProps & {
    phoneRequired?: boolean;
    loFullName?: string | null;
    loNmls?: string | null;
  }
) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(REGISTRATION_EVENT.submitted, {
    ...buildRegistrationFunnelBasePayload(props),
    PhoneRequired: props.phoneRequired ?? false,
    LoanOfficerName: props.loFullName ?? "Unknown",
    LoanOfficerNMLS: props.loNmls ?? "Unknown",
  });
}

export function trackFSRegistrationCompleted(
  props: RegistrationFunnelBaseProps & {
    outcome: Extract<AuthOutcome, "otp_required" | "completed">;
    isMfaSkipEnabled?: boolean;
  }
) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(REGISTRATION_EVENT.completed, {
    ...buildRegistrationFunnelBasePayload(props),
    Outcome: props.outcome,
    IsMfaSkipEnabled: props.isMfaSkipEnabled ?? false,
  });
}

export function trackFSRegistrationFailed(
  props: RegistrationFunnelBaseProps & {
    error?: unknown;
    FailureCode?: string | number;
  }
) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(REGISTRATION_EVENT.failed, {
    ...buildRegistrationFunnelBasePayload(props),
    FailureCode: props.FailureCode ?? toAuthFailureCode(props.error),
  });
}

export function trackFSRegistrationPagePhoneLO(props: {
  leadProvider?: string;
  salesChannel?: string;
  referringUrl?: string;
  loFullName?: string;
  loNmls?: string;
  state?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, salesChannel, referringUrl, loFullName, loNmls, state } = props;

  FullStory.event("Registration Page Phone LO", {
    LeadProvider: leadProvider ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    ReferringUrl: referringUrl ?? "Unknown",
    ...buildRegistrationSubmitLoProperties({ fullName: loFullName, nmlsLicenseNumber: loNmls }),
    State: state ?? "Unknown",
  });
}

export function trackFSRegistrationPageEmailLO(props: {
  leadProvider?: string;
  salesChannel?: string;
  referringUrl?: string;
  loFullName?: string;
  loNmls?: string;
  state?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, salesChannel, referringUrl, loFullName, loNmls, state } = props;

  FullStory.event("Registration Page Email LO", {
    LeadProvider: leadProvider ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    ReferringUrl: referringUrl ?? "Unknown",
    ...buildRegistrationSubmitLoProperties({ fullName: loFullName, nmlsLicenseNumber: loNmls }),
    State: state ?? "Unknown",
  });
}

const setFSNavigatedToInsuranceCustomEvent = (nhDealApplicationId: string, redirectionUrl: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NavigatedToInsurance", {
    nhDealApplicationId,
    redirectionUrl,
  });
};

interface BorrowerDashboardFullStoryProps {
  email?: string;
  sessionId?: string | null;
  dealId?: string | null;
  prospectId?: string | null;
  encompassLoanGuid?: string | null;
  encompassLoanNumber?: string | null;
  nhDealApplicationId?: string | null;
  loanPurpose?: string | null;
  milestone?: string | null;
  wasLoAssigned?: boolean;
  borrowerType?: string | null;
  borrowerRoleType?: string | null;
}

const buildBorrowerDashboardFullStoryPayload = (props?: BorrowerDashboardFullStoryProps) => ({
  Email: props?.email?.trim() || "Unknown",
  SessionID: props?.sessionId ?? "Unknown",
  DealID: props?.dealId ?? null,
  ProspectID: props?.prospectId ?? null,
  Timestamp: new Date().toISOString(),
  EncompassLoanGuid: props?.encompassLoanGuid ?? null,
  EncompassLoanNumber: props?.encompassLoanNumber ?? null,
  HubDealApplicationID: props?.nhDealApplicationId ?? "Unknown",
  LoanPurpose: props?.loanPurpose ?? "Unknown",
  WasLOAssigned: props?.wasLoAssigned ?? false,
});

export type InsuranceFullStoryProps = BorrowerDashboardFullStoryProps;

const trackFSInsuranceEvent = (eventName: string, props?: InsuranceFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    ...buildBorrowerDashboardFullStoryPayload(props),
    HubMilestone: props?.milestone ?? "Unknown",
    BorrowerRoleType: props?.borrowerRoleType ?? props?.borrowerType ?? "Unknown",
  });
};

export const trackFSInsuranceCardClick = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceCardClick", props);

export const trackFSInsuranceTaskCTAClick = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceTaskCTAClick", props);

export const trackFSInsuranceGetQuotes = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceGetQuotes", props);

export const trackFSInsuranceGetQuotesDismissed = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceGetQuotesDismissed", props);

export const trackFSInsuranceReferralCompleted = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceReferralCompleted", props);

export const trackFSInsuranceReferralDropoff = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsuranceReferralDropoff", props);

export const trackFSInsurancePostReferralThankYou = (props?: InsuranceFullStoryProps) =>
  trackFSInsuranceEvent("InsurancePostReferralThankYou", props);

const setFSUserProperties = (email?: string, lastName?: string) => {
  if (isFullStoryEnabled() && email) {
    FullStory.setUserVars({ email });
  }
  if (isFullStoryEnabled() && lastName) {
    FullStory.setUserVars({ lastName });
  }
};

interface AnalyticsSection {
  section: string;
  eventType: AnalyticsEvent;
  displayName: string;
}

export const analyticsSections: AnalyticsSection[] = [
  { section: "Lead", eventType: "FSLeadCompleted", displayName: "Lead Completed" },
  { section: "Profile", eventType: "FSProfileCompleted", displayName: "Profile Completed" },
  { section: "Property", eventType: "FSPropertyCompleted", displayName: "Property Completed" },
  { section: "Credit", eventType: "FSCreditCompleted", displayName: "Credit Completed" },
  { section: "Finances", eventType: "FSFinanceCompleted", displayName: "Finance Completed" },
  { section: "Acknowledgements", eventType: "FSDeclarationsCompleted", displayName: "Declarations Completed" },
];

export const trackFSNpsDisplayed = (props: {
  count: number;
  loanPurpose?: string;
  salesChannel?: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { count, loanPurpose, salesChannel } = props;

  FullStory.event("NPS Displayed", {
    Count: count,
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
  });
};

export const trackFSNpsCompleted = (props: { isComplete: boolean }) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NPS Completed", {
    NPSComplete: props.isComplete,
  });
};

export const trackFSNpsClosed = (props: {
  loanPurpose?: string;
  salesChannel?: string;
  npsScore?: number | null;
  npsFeedback?: string | null;
}) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, npsScore, npsFeedback } = props;

  FullStory.event("NPS Closed", {
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    NPSScore: typeof npsScore === "number" ? npsScore : null,
    NPSFeedback: npsFeedback ?? "",
  });
};

export const trackFSServicingCtaClicked = (userId?: string, nhDealApplicationId?: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Servicing CTA Clicked", {
    UserId: userId ?? "Unknown",
    NhDealApplicationId: nhDealApplicationId ?? "Unknown",
  });
};

export interface NativeAppFullStoryProps {
  username?: string;
  prospectId?: string;
  dealId?: string;
  losLoanNumber?: string;
  losGuid?: string;
  platform?: string;
}

const getClientPlatform = () => {
  if (typeof navigator === "undefined") return "Unknown";
  const userAgentData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return userAgentData?.platform || navigator.platform || "Unknown";
};

const trackFSNativeAppEvent = (eventName: string, props?: NativeAppFullStoryProps) => {
  const payload = {
    Username: props?.username ?? "Unknown",
    ProspectID: props?.prospectId ?? "Unknown",
    DealID: props?.dealId ?? "Unknown",
    Timestamp: new Date().toISOString(),
    EventName: eventName,
    LOSLoanNumber: props?.losLoanNumber ?? "Unknown",
    LOSGUID: props?.losGuid ?? "Unknown",
    Platform: props?.platform ?? getClientPlatform(),
  };

  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, payload);
};

export const trackFSNativeAppBannerClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Banner Clicked", props);

export const trackFSNativeAppBannerDismissed = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Banner Dismissed", props);

export const trackFSNativeAppModalDismissed = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal Dismissed", props);

export const trackFSNativeAppModalAppStoreClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal App Store Clicked", props);

export const trackFSNativeAppModalMaybeLaterClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal Maybe Later Clicked", props);

export const trackFSNativeAppAdClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Ad Clicked", props);

export type UqualFullStoryProps = BorrowerDashboardFullStoryProps;

const trackFSUqualEvent = (eventName: string, props?: UqualFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    ...buildBorrowerDashboardFullStoryPayload(props),
    Milestone: props?.milestone ?? "Unknown",
    BorrowerType: props?.borrowerType ?? props?.borrowerRoleType ?? "Unknown",
  });
};

export const trackFSUqualBannerStartNowClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Start Now in Banner", props);

export const trackFSUqualBannerDismissed = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Dismiss Banner", props);

export const trackFSUqualCardStartNowClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Start Now in Card", props);

export const trackFSUqualRemindMeLaterClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Remind Me Later", props);

export const trackFSUqualContactMeClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Contact Me", props);

interface ExitIntentBaseProps {
  loanPurpose?: string;
  salesChannel?: string;
  stepCode?: string;
  stepCodeName?: string;
  trigger?: string | null;
}

interface ExitIntentWithAnswersProps extends ExitIntentBaseProps {
  question1?: string;
  question2?: string;
}

const getExitIntentTriggerPayload = (trigger?: string | null) =>
  trigger === "BrowserBack" || trigger === "NavigateAway" ? trigger : undefined;

export const trackFSExitIntentDisplayed = (props: ExitIntentBaseProps) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName, trigger } = props;
  const mappedTrigger = getExitIntentTriggerPayload(trigger);
  if (!mappedTrigger) return;

  FullStory.event("Exit Intent Displayed", {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
    Trigger: mappedTrigger,
  });
};

function trackFSExitIntentWithAnswers(
  eventName: "Exit Intent Completed" | "Exit Intent Cancelled",
  props: ExitIntentWithAnswersProps
) {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName, question1, question2, trigger } = props;
  const mappedTrigger = getExitIntentTriggerPayload(trigger);
  if (!mappedTrigger) return;

  FullStory.event(eventName, {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
    "Question 1": question1 || "",
    "Question 2": question2 || "",
    Trigger: mappedTrigger,
  });
}

export const trackFSExitIntentCompleted = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Completed", props);

export const trackFSExitIntentCancelled = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Cancelled", props);

export const trackFSDocumentsUploaded = (props: {
  username: string;
  timestamp: string;
  prospectId: string;
  dealId: string | null;
  losLoanNumber: string | null;
  losId: string | null;
  numberOfDocumentsUploaded: number;
  loanMilestone: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { username, timestamp, prospectId, dealId, losLoanNumber, losId, numberOfDocumentsUploaded, loanMilestone } = props;

  FullStory.event("Documents Uploaded", {
    Username: username,
    Timestamp: timestamp,
    ProspectID: prospectId,
    DealID: dealId,
    LOSLoanNumber: losLoanNumber,
    LOSID: losId,
    NumberOfDocumentsUploaded: numberOfDocumentsUploaded,
    LoanMilestone: loanMilestone,
  });
};

interface SelfRegistrationTrackingProps {
  salesChannel?: string;
  loFullName?: string;
  loNMLS?: string;
  loanPurpose?: string;
  state?: string;
  rejectedReason?: string;
  FailureCode?: string | number;
}

function buildSelfRegistrationEventPayload(
  props: SelfRegistrationTrackingProps,
  additionalProps?: Record<string, unknown>
) {
  return {
    "Sales Channel": props.salesChannel ?? "Unknown",
    ...buildRegistrationSubmitLoProperties({ fullName: props.loFullName, nmlsLicenseNumber: props.loNMLS }),
    "Loan Purpose": props.loanPurpose ?? "Unknown",
    State: props.state ?? "Unknown",
    ...additionalProps,
  };
}

export const trackFSVisitedSelfRegistration = (props: Omit<SelfRegistrationTrackingProps, "rejectedReason" | "FailureCode">) => {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Visited Self-Registration", buildSelfRegistrationEventPayload(props));
};

export const trackFSCompletedSelfRegistration = (props: Omit<SelfRegistrationTrackingProps, "rejectedReason" | "FailureCode">) =>
  trackFSAuthEvent("Completed Self-Registration", {
    flow_context: FLOW_CONTEXT.self_registration,
    Outcome: AUTH_OUTCOME.completed,
    ...buildSelfRegistrationEventPayload(props, { Completed: true }),
  });

export const trackFSRejectedSelfRegistration = (props: SelfRegistrationTrackingProps) =>
  trackFSAuthEvent("Rejected Self-Registration", {
    flow_context: FLOW_CONTEXT.self_registration,
    Outcome: AUTH_OUTCOME.failure,
    FailureCode: props.FailureCode ?? toAuthFailureCode(props.rejectedReason),
    ...buildSelfRegistrationEventPayload(props, {
      Completed: false,
      "Rejected Reason": props.rejectedReason ?? "Unknown",
    }),
  });

type ESignFullStoryProps = {
  username?: string;
  prospectId?: string;
  dealId?: string | null;
  timestamp: string;
  eventName?: string;
  losLoanNumber?: string | null;
  losGuid?: string | null;
  loanMilestone?: string;
};

const E_SIGN_EVENT_NAMES = {
  taskLoaded: "E-Sign TaskLoaded",
  started: "E-Sign Started",
  completed: "E-Sign Completed",
} as const;

type ESignEventName = (typeof E_SIGN_EVENT_NAMES)[keyof typeof E_SIGN_EVENT_NAMES];

const trackFSESignEvent = (eventName: ESignEventName, props: ESignFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    Username: props.username ?? "Unknown",
    ProspectID: props.prospectId ?? "Unknown",
    DealID: props.dealId ?? "Unknown",
    Timestamp: props.timestamp,
    EventName: props.eventName ?? eventName,
    LOSLoanNumber: props.losLoanNumber ?? "Unknown",
    LOSGUID: props.losGuid ?? "Unknown",
    LoanMilestone: props.loanMilestone ?? "Unknown",
  });
};

export const trackFSESignTaskLoaded = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.taskLoaded, props);

export const trackFSESignStarted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.started, props);

export const trackFSESignCompleted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.completed, props);

export const GUIDED_EXPERIENCE_EVENT_NAMES = {
  loAssignedTasksOutstanding: "LO_ASSIGNED_TASKS_OUTSTANDING",
  noLoBusinessHours: "NO_LO_BUSINESS_HOURS",
  noLoNotBusinessHours: "NO_LO_NOT_BUSINESS_HOURS",
} as const;

export type GuidedExperienceTaskCompleteState =
  | typeof GUIDED_EXPERIENCE_EVENT_NAMES.loAssignedTasksOutstanding
  | typeof GUIDED_EXPERIENCE_EVENT_NAMES.noLoNotBusinessHours;

export interface GuidedExperienceFullStoryProps {
  username?: string;
  prospectId?: string;
  dealId?: string | null;
  losLoanNumber?: string | null;
}

const trackFSGuidedExperienceEvent = (eventName: string, props?: GuidedExperienceFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    Username: props?.username ?? "Unknown",
    ProspectID: props?.prospectId ?? "Unknown",
    DealID: props?.dealId ?? "Unknown",
    Timestamp: new Date().toISOString(),
    EventName: eventName,
    LOSLoanNumber: props?.losLoanNumber ?? "Unknown",
  });
};

/** Mark that user saw a guided tooltip that converts on first task complete. */
export const markGuidedExperienceTaskCompleteSeen = (state: GuidedExperienceTaskCompleteState) => {
  try {
    sessionStorage.setItem(GUIDED_EXP_TASK_COMPLETE_SEEN_STATE, state);
  } catch {
    // sessionStorage unavailable — skip; gate will no-op without seen state
  }
};

/**
 * Fire guided-experience FS event only when:
 * - user saw LO_ASSIGNED_TASKS_OUTSTANDING or NO_LO_NOT_BUSINESS_HOURS tooltip, and
 * - first task completion after that has not been tracked yet.
 */
export const trackFSGuidedExperienceFirstTaskCompletedIfEligible = (
  props?: GuidedExperienceFullStoryProps
) => {
  try {
    if (sessionStorage.getItem(GUIDED_EXP_TASK_COMPLETE_TRACKED) === "true") return;

    const seenState = sessionStorage.getItem(GUIDED_EXP_TASK_COMPLETE_SEEN_STATE);
    if (
      seenState !== GUIDED_EXPERIENCE_EVENT_NAMES.loAssignedTasksOutstanding &&
      seenState !== GUIDED_EXPERIENCE_EVENT_NAMES.noLoNotBusinessHours
    ) {
      return;
    }

    if (!isFullStoryEnabled()) return;

    trackFSGuidedExperienceEvent(seenState, props);
    sessionStorage.setItem(GUIDED_EXP_TASK_COMPLETE_TRACKED, "true");
  } catch {
    // never block task completion UX
  }
};

/** Call Now click under NO_LO_BUSINESS_HOURS guided experience. */
export const trackFSGuidedExperienceCallNowClicked = (props?: GuidedExperienceFullStoryProps) => {
  trackFSGuidedExperienceEvent(GUIDED_EXPERIENCE_EVENT_NAMES.noLoBusinessHours, props);
};

export interface PageNavigationFullStoryProps {
  email?: string;
  prospectId?: string;
  sessionId?: string | null;
  sessionStartTimestamp?: string | null;
  pageName: string;
  loanNumber?: string | null;
  loanGuid?: string | null;
  dealId?: string | null;
  loSalesDomain?: string;
}

export const trackFSDashboardPageNavigated = (props: PageNavigationFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  const { email, prospectId, sessionId, sessionStartTimestamp, pageName, loanNumber, loanGuid, dealId, loSalesDomain } = props;

  FullStory.event("Dashboard Page Navigated", {
    Email: email ?? "Unknown",
    ProspectID: prospectId ?? "Unknown",
    SessionID: sessionId ?? "Unknown",
    SessionStartTimestamp: sessionStartTimestamp ?? "Unknown",
    NavigationEventTimestamp: new Date().toISOString(),
    PageName: pageName,
    LoanNumber: loanNumber ?? null,
    LoanGUID: loanGuid ?? null,
    DealID: dealId ?? null,
    LOSalesDomain: loSalesDomain ?? "Unassigned",
  });
};

export type ShellContext = "dashboard" | "application" | "home" | "my-loans" | "settings";
export type ShellViewport = "tablet" | "mobile" | "desktop";

export interface ShellAnalyticsContext {
  email?: string;
  prospectId?: string;
  dealId?: string | null;
  loanNumber?: string | null;
  loanGuid?: string | null;
}

export interface ShellAnalyticsProps extends ShellAnalyticsContext {
  sessionId?: string | null;
  sessionStartTimestamp?: string | null;
  viewport?: ShellViewport;
  shellContext?: ShellContext;
}

export const tagFSShellLayoutSession = (shellContext?: ShellContext) => {
  if (!isFullStoryEnabled()) return;

  FullStory.setUserVars({
    shellLayout: "global-navigation-shell",
    ...(shellContext ? { shellContext } : {}),
  });
};

export const trackFSNavShellSidebarOpen = (props: ShellAnalyticsProps) => {
  if (!isFullStoryEnabled()) return;

  const { email, prospectId, sessionId, sessionStartTimestamp, dealId, loanNumber, loanGuid, viewport, shellContext } = props;

  FullStory.event("nav_shell_sidebar_open", {
    Email: email ?? "Unknown",
    ProspectID: prospectId ?? "Unknown",
    SessionID: sessionId ?? "Unknown",
    SessionStartTimestamp: sessionStartTimestamp ?? "Unknown",
    NavigationEventTimestamp: new Date().toISOString(),
    Viewport: viewport ?? "tablet",
    ShellContext: shellContext ?? "Unknown",
    DealID: dealId ?? null,
    LoanNumber: loanNumber ?? null,
    LoanGUID: loanGuid ?? null,
  });
};

export type BottomNavTabId = "home" | "tasks" | "documents" | "more" | "loans" | "settings";

export interface BottomNavTabSelectProps {
  tabId: BottomNavTabId;
}

export const trackFSNavShellBottomNavTabSelect = ({ tabId }: BottomNavTabSelectProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("nav_shell_bottom_nav_tab_select", {
    tabId,
    NavigationEventTimestamp: new Date().toISOString(),
  });
};

export type LanguagePreference = "Spanish" | "English";

export function trackFSLanguagePreferenceChanged(props: {
  fromLanguage: LanguagePreference;
  toLanguage: LanguagePreference;
  applicationStep?: string;
  applicationId?: string | null;
  loanNumber?: string | null;
  changeSource: "user_selector";
  acknowledgmentLanguage?: "spanish" | "english";
}) {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Language Preference Changed", {
    FromLanguage: props.fromLanguage,
    SelectedLanguage: props.toLanguage,
    ApplicationStep: props.applicationStep ?? null,
    ApplicationId: props.applicationId ?? null,
    LoanNumber: props.loanNumber ?? null,
    ChangeSource: props.changeSource,
    AcknowledgmentLanguage: props.acknowledgmentLanguage ?? null,
    Environment: globalThis.env.ENVIRONMENT ?? "Unknown",
  });
}

export type LoProfileCardSurface = "dashboard" | "application" | "registration" | "form";

export interface LoProfileCardFullStoryProps {
  loFullName?: string | null;
  loTitle?: string | null;
  loNmls?: string | null;
  loEmail?: string | null;
  loPhone?: string | null;
  channel?: string | null;
  phonetype?: string | null;
  hasHeadshot: boolean;
  headshotUrl?: string | null;
  headshotLoadError: boolean;
  surface: LoProfileCardSurface;
  dataSource?: string | null;
  isCallQueue: boolean;
  featureFlag: "on" | "off";
  hasLoData: boolean;
  dealId?: string | null;
  bffStatus?: string | null;
  outcome?: string | null;
  cacheHit: boolean;
}

export function trackFSLoProfileCardRendered(props: LoProfileCardFullStoryProps) {
  if (!isFullStoryEnabled()) return;

  FullStory.event("lo_profile_card_rendered", {
    loFullName: props.loFullName ?? null,
    loTitle: props.loTitle ?? null,
    loNmls: props.loNmls ?? null,
    loEmail: props.loEmail ?? null,
    loPhone: props.loPhone ?? null,
    channel: props.channel ?? null,
    phonetype: props.phonetype ?? null,
    hasHeadshot: props.hasHeadshot,
    headshotUrl: props.headshotUrl ?? null,
    headshotLoadError: props.headshotLoadError,
    surface: props.surface,
    dataSource: props.dataSource ?? null,
    isCallQueue: props.isCallQueue,
    featureFlag: props.featureFlag,
    hasLoData: props.hasLoData,
    dealId: props.dealId ?? null,
    bffStatus: props.bffStatus ?? null,
    outcome: props.outcome ?? null,
    cacheHit: props.cacheHit,
  });
}

export {
  initFullStory,
  setFSUserProperties,
  setFSNavigatedToInsuranceCustomEvent,
  trackFSSummaryEvent,
  trackFSSummaryEditEvent,
  trackFSOptedVoaEvent,
  trackFSLOAssignedEvent,
  trackFSOptedVoiEvent,
};
